import Box from '@mui/material/Box';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import MenuItem from '@mui/material/MenuItem';
import Pagination from '@mui/material/Pagination';
import Select from '@mui/material/Select';
import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { useId, type ReactElement } from 'react';

const DEFAULT_PAGE_SIZE_OPTIONS = [10, 25, 50] as const;
const DESKTOP = 'md';

export type ListPaginationProps = {
    page: number;
    pageSize: number;
    total: number;
    pageSizeOptions?: readonly number[];
    onPageChange: (page: number) => void;
    onPageSizeChange: (pageSize: number) => void;
    itemsPerPageLabel: string;
    paginationAriaLabel: string;
};

export function ListPagination({
    page,
    pageSize,
    total,
    pageSizeOptions = DEFAULT_PAGE_SIZE_OPTIONS,
    onPageChange,
    onPageSizeChange,
    itemsPerPageLabel,
    paginationAriaLabel,
}: ListPaginationProps): ReactElement {
    const theme = useTheme();
    const pageSizeLabelId = useId();
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    // A phone-width row cannot hold the jump-to-edge buttons on top of the page numbers.
    const compact = useMediaQuery(theme.breakpoints.down(DESKTOP));

    return (
        <Box
            sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: { xs: 'center', [DESKTOP]: 'space-between' },
                flexWrap: 'wrap',
                gap: 2,
                width: '100%',
            }}>
            <Pagination
                count={pageCount}
                page={page + 1}
                onChange={(_event, nextPage) => onPageChange(nextPage - 1)}
                color="primary"
                siblingCount={compact ? 0 : 1}
                showFirstButton={!compact}
                showLastButton={!compact}
                aria-label={paginationAriaLabel}
            />
            <FormControl size="small" sx={{ minWidth: 140 }}>
                <InputLabel id={pageSizeLabelId}>{itemsPerPageLabel}</InputLabel>
                <Select
                    labelId={pageSizeLabelId}
                    label={itemsPerPageLabel}
                    value={pageSize}
                    onChange={(event) => onPageSizeChange(Number(event.target.value))}>
                    {pageSizeOptions.map((option) => (
                        <MenuItem key={option} value={option}>
                            {option}
                        </MenuItem>
                    ))}
                </Select>
            </FormControl>
        </Box>
    );
}
